import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Loader2, Plus, Sparkles, Compass, Send, Flame, Users as UsersIcon } from 'lucide-react';
import PostCard from './PostCard';
import CreatePostModal from './CreatePostModal';
import ProfileSheet from './ProfileSheet';
import StoriesTray from './StoriesTray';
import MessagesInbox from './MessagesInbox';
import TestimoniesReel from './TestimoniesReel';
import FeaturedTestimonyCard from './FeaturedTestimonyCard';
import { postsApi, mediaUrl } from '../services/postsApi';
import { messagesApi } from '../services/messagesApi';
import SuggestedForYou from './community/SuggestedForYou';
import StarterFollowDialog from './community/StarterFollowDialog';

const MODES = [
  { id: 'for_you', icon: Flame, en: 'For You', es: 'Para ti' },
  { id: 'following', icon: UsersIcon, en: 'Following', es: 'Siguiendo', auth: true },
  { id: 'explore', icon: Compass, en: 'Explore', es: 'Explorar' },
];

/**
 * PostFeed — the Instagram-style surface for Lumera Community.
 * Two modes: "feed" (following + friends + public, single column) and
 * "explore" (popular public posts, grid). Includes stories, create-post + profiles.
 */
const PostFeed = ({ language = 'en', isGuest, currentUserId, requireAuth }) => {
  const es = language === 'es';
  const [mode, setMode] = useState('for_you');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDest, setCreateDest] = useState('feed');
  const [profileUser, setProfileUser] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [inboxFocus, setInboxFocus] = useState(null);
  const [unread, setUnread] = useState(0);
  const trayRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = mode === 'explore' ? await postsApi.explore() : await postsApi.feed(mode);
      setPosts(data);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  }, [mode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('lumera:pending-action-done', load);
    return () => window.removeEventListener('lumera:pending-action-done', load);
  }, [load]);
  // Return a guest to the post they interacted with before signing in.
  useEffect(() => {
    if (loading) return;
    const id = sessionStorage.getItem('lumera_scroll_to');
    const el = id && document.querySelector(`[data-testid="post-card-${id}"]`);
    if (el) { sessionStorage.removeItem('lumera_scroll_to'); setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }
  }, [loading, posts]);

  const loadUnread = useCallback(async () => {
    if (isGuest) return;
    try { const u = await messagesApi.unread(); setUnread((u.unread || 0) + (u.requests || 0)); } catch { setUnread(0); }
  }, [isGuest]);
  useEffect(() => { loadUnread(); }, [loadUnread]);
  useEffect(() => {
    if (isGuest) return;
    const id = setInterval(loadUnread, 20000);
    return () => clearInterval(id);
  }, [isGuest, loadUnread]);
  useEffect(() => {
    const open = (e) => { setInboxFocus(e?.detail?.focus || null); setMsgOpen(true); };
    window.addEventListener('lumera:open-messages', open);
    return () => window.removeEventListener('lumera:open-messages', open);
  }, []);

  const handleCreated = (post) => setPosts((p) => [post, ...p]);
  const handleStoryCreated = () => trayRef.current?.reload();
  const openCreate = (dest = 'feed') => {
    if (isGuest) { requireAuth?.(es ? 'Crear publicaciones' : 'Creating posts'); return; }
    setCreateDest(dest); setCreateOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="post-feed">
      <StoriesTray ref={trayRef} language={language} isGuest={isGuest} currentUserId={currentUserId} onAddStory={() => openCreate('story')} />

      <div className="flex items-center gap-2">
        <div className="flex rounded-full bg-muted p-1">
          {MODES.filter((m) => !m.auth || !isGuest).map(({ id, icon: Icon, en, es: esLabel }) => (
            <button key={id} onClick={() => setMode(id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${mode === id ? 'bg-background shadow' : 'text-muted-foreground'}`}
              data-testid={`postfeed-mode-${id}`}><Icon className="w-4 h-4" /> {es ? esLabel : en}</button>
          ))}
        </div>
        <Button onClick={() => openCreate('feed')} className="ml-auto rounded-full" size="sm" data-testid="postfeed-create-btn">
          <Plus className="w-4 h-4 mr-1" /> {es ? 'Crear' : 'Post'}
        </Button>
        {!isGuest && (
          <button onClick={() => { setMsgOpen(true); }} className="relative rounded-full p-2 hover:bg-muted transition-colors" data-testid="postfeed-messages-btn" aria-label={es ? 'Mensajes' : 'Messages'}>
            <Send className="w-5 h-5" />
            {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center" data-testid="postfeed-messages-badge">{unread}</span>}
          </button>
        )}
      </div>

      {!isGuest && <StarterFollowDialog language={language} currentUserId={currentUserId} onDone={load} />}
      {mode === 'for_you' && !loading && <TestimoniesReel language={language} />}
      {mode === 'for_you' && !loading && (
        <div className="max-w-lg mx-auto"><FeaturedTestimonyCard language={language} onOpen={(p) => setProfileUser(p.user_id)} /></div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-2xl" data-testid="postfeed-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-primary/60" />
          <p className="text-muted-foreground mb-4">
            {mode === 'explore'
              ? (es ? 'Aún no hay publicaciones para explorar.' : 'No posts to explore yet.')
              : (es ? 'Tu feed está vacío. ¡Sigue a personas o crea tu primera publicación!' : 'Your feed is empty. Follow people or create your first post!')}
          </p>
          <Button onClick={() => openCreate('feed')} data-testid="postfeed-empty-create"><Plus className="w-4 h-4 mr-1" /> {es ? 'Crear publicación' : 'Create a post'}</Button>
        </div>
      ) : mode === 'explore' ? (
        <div className="grid grid-cols-3 gap-1" data-testid="postfeed-explore-grid">
          {posts.map((p) => (
            <button key={p.id} onClick={() => setProfileUser(p.user_id)} className="aspect-square overflow-hidden bg-muted group relative">
              <img src={mediaUrl(p.image_url)} alt={p.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" onError={(e) => { e.currentTarget.closest('button').style.display = 'none'; }} />
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-5 max-w-lg mx-auto">
          {posts.map((p, i) => (
            <React.Fragment key={p.id}>
            {i === 2 && !isGuest && <SuggestedForYou language={language} onOpenProfile={(uid) => setProfileUser(uid)} />}
            <PostCard
              post={p}
              language={language}
              currentUserId={currentUserId}
              isGuest={isGuest}
              requireAuth={requireAuth}
              onOpenProfile={(uid) => setProfileUser(uid)}
              onDeleted={(id) => setPosts((list) => list.filter((x) => x.id !== id))}
              onUpdated={(id, caption) => setPosts((list) => list.map((x) => (x.id === id ? { ...x, caption } : x)))}
            />
            </React.Fragment>
          ))}
          {posts.length < 3 && !isGuest && <SuggestedForYou language={language} onOpenProfile={(uid) => setProfileUser(uid)} />}
        </div>
      )}

      <CreatePostModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} onStoryCreated={handleStoryCreated} defaultDestination={createDest} language={language} />
      <ProfileSheet userId={profileUser} open={!!profileUser} onClose={() => setProfileUser(null)} language={language} />
      <MessagesInbox open={msgOpen} focus={inboxFocus} onClose={() => { setMsgOpen(false); setInboxFocus(null); loadUnread(); }} language={language} currentUserId={currentUserId} />
    </div>
  );
};

export default PostFeed;
